/**
 * <PolicyTemplateEdit /> — admin editor for a single onboarding template.
 *
 * Routes:
 *   /admin/policy-templates/new         — create a new template
 *   /admin/policy-templates/:templateId — edit an existing template
 *
 * Capabilities:
 *   - Rename / re-version / activate / archive the template
 *   - List the policies (items) on the template
 *   - Add an item: pick an existing policy_document or create a new one inline,
 *                  or add a manager-driven check (no policy_document)
 *   - Edit an item: title, description, type, required, driver, order
 *   - Reorder items (move up / move down)
 *   - Remove an item from the template (does NOT delete the underlying
 *     policy_document, which is append-only per firestore.rules)
 *
 * Note on deletes: firestore.rules deny deletes on both onboarding_templates
 * and policy_documents. Items inside a template are an array on the template
 * doc, so they can be hard-removed via updateDoc.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { useToast } from '../../../components/Toast';
import {
  getTemplate,
  createTemplate,
  updateTemplateMeta,
  setTemplateItems,
  listAllPolicyDocuments,
  createPolicyDocument,
} from '../services/onboardingService';
import '../Onboarding.css';

const ITEM_TYPES = [
  { value: 'esign',           label: 'Policy e-signature (employee signs)' },
  { value: 'manager_verify',  label: 'Manager verification (manager checks off)' },
  { value: 'upload',          label: 'Document upload (employee uploads file)' },
];

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

function blankItem(nextOrder) {
  return {
    id: '',
    type: 'esign',
    title: '',
    description: '',
    required: true,
    driver: 'employee',
    order: nextOrder,
    policy_document_id: '',
    upload_config: null,
  };
}

export default function PolicyTemplateEdit() {
  const navigate = useNavigate();
  const toast = useToast();
  const { templateId } = useParams();
  const isNew = !templateId || templateId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [savingItems, setSavingItems] = useState(false);

  const [meta, setMeta] = useState({
    id: '',
    name: '',
    version: '1.0.0',
    active: true,
  });
  const [items, setItems] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [showNewPolicy, setShowNewPolicy] = useState(null); // index of item being authored against
  const [newPolicy, setNewPolicy] = useState({ id: '', title: '', body_markdown: '', pdf_url: '' });
  const [errors, setErrors] = useState({});

  // Load template + available policy_documents
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const pols = await listAllPolicyDocuments();
        if (active) setPolicies(pols.sort((a, b) => (a.title || a.id).localeCompare(b.title || b.id)));
      } catch (e) {
        toast.error('Failed to load policies: ' + (e.message || e));
      }
      if (isNew) return;
      try {
        const tpl = await getTemplate(templateId);
        if (!tpl) {
          toast.error('Template not found');
          navigate('/admin/policy-templates');
          return;
        }
        if (active) {
          setMeta({
            id: tpl.id,
            name: tpl.name || '',
            version: tpl.version || '1.0.0',
            active: tpl.active !== false,
          });
          setItems(Array.isArray(tpl.items) ? [...tpl.items].sort((a, b) => (a.order || 0) - (b.order || 0)) : []);
        }
      } catch (e) {
        toast.error('Failed to load template: ' + (e.message || e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [templateId, isNew, navigate, toast]);

  // ---- meta save -------------------------------------------------------
  const saveMeta = async () => {
    const errs = {};
    if (!meta.name.trim()) errs.name = 'Name is required';
    if (isNew && !meta.id.trim()) errs.id = 'Id is required';
    if (isNew && !/^[a-z0-9_]+$/.test(meta.id)) errs.id = 'Use lowercase letters, digits, and underscores only';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      if (isNew) {
        await createTemplate({
          id: meta.id,
          name: meta.name,
          version: meta.version,
          active: meta.active,
          items: [],
        });
        toast.success('Template created');
        navigate(`/admin/policy-templates/${meta.id}`, { replace: true });
      } else {
        await updateTemplateMeta(meta.id, {
          name: meta.name,
          version: meta.version,
          active: meta.active,
        });
        toast.success('Template saved');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ---- item editing ----------------------------------------------------
  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const addItem = () => {
    setItems((prev) => {
      const nextOrder = prev.reduce((m, it) => Math.max(m, it.order || 0), 0) + 1;
      return [...prev, blankItem(nextOrder)];
    });
  };

  const removeItem = (idx) => {
    if (!window.confirm('Remove this policy from the template?')) return;
    setItems((prev) => reorder(prev.filter((_, i) => i !== idx)));
  };

  const move = (idx, dir) => {
    setItems((prev) => {
      const next = [...prev];
      const swap = idx + dir;
      if (swap < 0 || swap >= next.length) return prev;
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return reorder(next);
    });
  };

  const itemsValid = useMemo(() => {
    return items.every((it) => {
      if (!it.title?.trim()) return false;
      if (!it.id?.trim()) return false;
      if (it.type === 'esign' && !it.policy_document_id) return false;
      return true;
    });
  }, [items]);

  const saveItems = async () => {
    if (!itemsValid) {
      toast.error('Each item needs an id, title, and (for e-sign) a linked policy');
      return;
    }
    // dedupe item ids
    const ids = new Set();
    for (const it of items) {
      if (ids.has(it.id)) {
        toast.error(`Duplicate item id: ${it.id}`);
        return;
      }
      ids.add(it.id);
    }
    setSavingItems(true);
    try {
      const payload = items.map((it, i) => ({
        id: it.id.trim(),
        type: it.type,
        title: it.title.trim(),
        description: (it.description || '').trim(),
        required: it.required !== false,
        driver: it.driver || (it.type === 'manager_verify' ? 'manager' : 'employee'),
        order: i + 1,
        policy_document_id: it.type === 'esign' ? it.policy_document_id : null,
        upload_config: it.type === 'upload' ? (it.upload_config || {}) : null,
      }));
      await setTemplateItems(meta.id, payload);
      setItems(payload);
      toast.success('Items saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save items');
    } finally {
      setSavingItems(false);
    }
  };

  // ---- inline new-policy creation -------------------------------------
  const openNewPolicy = (idx, suggestedTitle = '') => {
    setShowNewPolicy(idx);
    setNewPolicy({
      id: suggestedTitle ? slugify(suggestedTitle) : '',
      title: suggestedTitle,
      body_markdown: '',
      pdf_url: '',
    });
  };

  const createPolicyAndAttach = async () => {
    try {
      if (!newPolicy.id || !/^[a-z0-9_]+$/.test(newPolicy.id)) {
        toast.error('Policy id must be lowercase letters/digits/underscores');
        return;
      }
      await createPolicyDocument({
        id: newPolicy.id,
        title: newPolicy.title,
        body_markdown: newPolicy.body_markdown,
        pdf_url: newPolicy.pdf_url,
      });
      toast.success('Policy created');
      const pols = await listAllPolicyDocuments();
      setPolicies(pols.sort((a, b) => (a.title || a.id).localeCompare(b.title || b.id)));
      if (showNewPolicy !== null) {
        updateItem(showNewPolicy, {
          policy_document_id: newPolicy.id,
          title: items[showNewPolicy].title || newPolicy.title,
          id: items[showNewPolicy].id || newPolicy.id,
        });
      }
      setShowNewPolicy(null);
    } catch (e) {
      toast.error(e.message || 'Failed to create policy');
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner-large" /><p>Loading…</p></div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 1000, margin: '0 auto' }}>
      <button
        className="onboarding-btn secondary"
        onClick={() => navigate('/admin/policy-templates')}
        style={{ marginBottom: 12 }}
      >
        <ArrowLeft size={14} /> Back to templates
      </button>

      <h1 style={{ fontFamily: 'Fraunces, Georgia, serif', margin: 0 }}>
        {isNew ? 'New template' : meta.name || meta.id}
      </h1>
      <p style={{ color: '#555', margin: '6px 0 16px', fontSize: '0.85rem' }}>
        {isNew
          ? 'Create a new onboarding template. After saving the basics you can add policies and manager checks.'
          : 'Edit this template. Changes to items take effect for future onboardings — already-assigned checklists are not retroactively updated.'}
      </p>

      {/* ---------------- META ---------------- */}
      <section style={sectionStyle}>
        <h2 style={h2Style}>Template details</h2>
        <div style={fieldRow}>
          <label style={labelStyle}>Name</label>
          <input
            style={inputStyle}
            value={meta.name}
            onChange={(e) => setMeta({ ...meta, name: e.target.value })}
            placeholder="Residential Cleaner v1"
          />
          {errors.name && <div className="field-error">{errors.name}</div>}
        </div>
        <div style={fieldRow}>
          <label style={labelStyle}>Id</label>
          <input
            style={{ ...inputStyle, fontFamily: 'monospace' }}
            value={meta.id}
            onChange={(e) => setMeta({ ...meta, id: slugify(e.target.value) })}
            placeholder="residential_cleaner_v2"
            disabled={!isNew}
          />
          {errors.id && <div className="field-error">{errors.id}</div>}
          {!isNew && <small style={{ color: '#888' }}>The id cannot be changed after creation.</small>}
        </div>
        <div style={fieldRow}>
          <label style={labelStyle}>Version</label>
          <input
            style={inputStyle}
            value={meta.version}
            onChange={(e) => setMeta({ ...meta, version: e.target.value })}
            placeholder="1.0.0"
          />
        </div>
        <div style={fieldRow}>
          <label style={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={meta.active}
              onChange={(e) => setMeta({ ...meta, active: e.target.checked })}
            />
            Active (available when assigning onboarding)
          </label>
        </div>
        <button className="onboarding-btn" onClick={saveMeta} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving…' : isNew ? 'Create template' : 'Save details'}
        </button>
      </section>

      {/* ---------------- ITEMS ---------------- */}
      {!isNew && (
        <section style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={h2Style}>Policies & checks ({items.length})</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="onboarding-btn secondary" onClick={addItem}>
                <Plus size={14} /> Add item
              </button>
              <button className="onboarding-btn" onClick={saveItems} disabled={savingItems || !items.length}>
                <Save size={14} /> {savingItems ? 'Saving…' : 'Save items'}
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <div className="onboarding-empty" style={{ marginTop: 12 }}>
              No items yet. Click "Add item" to add a policy or manager check.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
              {items.map((it, idx) => (
                <ItemEditor
                  key={`${idx}-${it.id || 'new'}`}
                  item={it}
                  index={idx}
                  total={items.length}
                  policies={policies}
                  onChange={(patch) => updateItem(idx, patch)}
                  onRemove={() => removeItem(idx)}
                  onMoveUp={() => move(idx, -1)}
                  onMoveDown={() => move(idx, +1)}
                  onCreateNewPolicy={(suggested) => openNewPolicy(idx, suggested)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ---------------- INLINE NEW POLICY DIALOG ---------------- */}
      {showNewPolicy !== null && (
        <div style={overlayStyle} onClick={() => setShowNewPolicy(null)}>
          <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ ...h2Style, marginTop: 0 }}>Create new policy document</h3>
            <p style={{ color: '#555', fontSize: '0.85rem', marginTop: 0 }}>
              This creates a new entry in <code>policy_documents</code>. The body is markdown and is
              shown to the employee on the e-signature screen.
            </p>
            <div style={fieldRow}>
              <label style={labelStyle}>Title</label>
              <input
                style={inputStyle}
                value={newPolicy.title}
                onChange={(e) =>
                  setNewPolicy({
                    ...newPolicy,
                    title: e.target.value,
                    id: newPolicy.id || slugify(e.target.value),
                  })
                }
              />
            </div>
            <div style={fieldRow}>
              <label style={labelStyle}>Id</label>
              <input
                style={{ ...inputStyle, fontFamily: 'monospace' }}
                value={newPolicy.id}
                onChange={(e) => setNewPolicy({ ...newPolicy, id: slugify(e.target.value) })}
                placeholder="vehicle_use_2026"
              />
            </div>
            <div style={fieldRow}>
              <label style={labelStyle}>PDF URL (optional)</label>
              <input
                style={inputStyle}
                value={newPolicy.pdf_url}
                onChange={(e) => setNewPolicy({ ...newPolicy, pdf_url: e.target.value })}
                placeholder="https://…"
              />
            </div>
            <div style={fieldRow}>
              <label style={labelStyle}>Body (markdown)</label>
              <textarea
                style={{ ...inputStyle, minHeight: 220, fontFamily: 'ui-monospace, monospace', fontSize: '0.85rem' }}
                value={newPolicy.body_markdown}
                onChange={(e) => setNewPolicy({ ...newPolicy, body_markdown: e.target.value })}
                placeholder={'# Policy title\n\n## Section\n\nText…'}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="onboarding-btn secondary" onClick={() => setShowNewPolicy(null)}>
                Cancel
              </button>
              <button className="onboarding-btn" onClick={createPolicyAndAttach}>
                Create & attach
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemEditor({ item, index, total, policies, onChange, onRemove, onMoveUp, onMoveDown, onCreateNewPolicy }) {
  const isEsign = item.type === 'esign';
  return (
    <div style={{ border: '1px solid #e5e5e5', borderRadius: 12, padding: 14, background: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
          #{index + 1} {item.title || <span style={{ color: '#aaa' }}>(untitled)</span>}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className="onboarding-btn secondary" onClick={onMoveUp} disabled={index === 0} title="Move up">
            <ArrowUp size={14} />
          </button>
          <button className="onboarding-btn secondary" onClick={onMoveDown} disabled={index === total - 1} title="Move down">
            <ArrowDown size={14} />
          </button>
          <button className="onboarding-btn danger" onClick={onRemove} title="Remove from template">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
        <div style={fieldRow}>
          <label style={labelStyle}>Type</label>
          <select
            style={inputStyle}
            value={item.type}
            onChange={(e) => {
              const type = e.target.value;
              onChange({
                type,
                driver: type === 'manager_verify' ? 'manager' : 'employee',
                policy_document_id: type === 'esign' ? item.policy_document_id : '',
              });
            }}
          >
            {ITEM_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div style={fieldRow}>
          <label style={labelStyle}>Item id</label>
          <input
            style={{ ...inputStyle, fontFamily: 'monospace' }}
            value={item.id}
            onChange={(e) => onChange({ id: slugify(e.target.value) })}
            placeholder="handbook_2026"
          />
        </div>

        <div style={{ ...fieldRow, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Title</label>
          <input
            style={inputStyle}
            value={item.title}
            onChange={(e) =>
              onChange({
                title: e.target.value,
                id: item.id || slugify(e.target.value),
              })
            }
            placeholder="Employee Handbook (2026)"
          />
        </div>

        <div style={{ ...fieldRow, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Description</label>
          <textarea
            style={{ ...inputStyle, minHeight: 60 }}
            value={item.description || ''}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Read and acknowledge the employee handbook."
          />
        </div>

        {isEsign && (
          <div style={{ ...fieldRow, gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Linked policy document</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                style={{ ...inputStyle, flex: 1 }}
                value={item.policy_document_id || ''}
                onChange={(e) => onChange({ policy_document_id: e.target.value })}
              >
                <option value="">— Select a policy —</option>
                {policies.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title || p.id} {p.active === false ? '(archived)' : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="onboarding-btn secondary"
                onClick={() => onCreateNewPolicy(item.title)}
                title="Create a new policy document and attach it"
              >
                <Plus size={14} /> New policy
              </button>
            </div>
          </div>
        )}

        <div style={fieldRow}>
          <label style={{ ...labelStyle, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={item.required !== false}
              onChange={(e) => onChange({ required: e.target.checked })}
            />
            Required
          </label>
        </div>

        <div style={fieldRow}>
          <label style={labelStyle}>Driver</label>
          <select
            style={inputStyle}
            value={item.driver || (item.type === 'manager_verify' ? 'manager' : 'employee')}
            onChange={(e) => onChange({ driver: e.target.value })}
          >
            <option value="employee">Employee</option>
            <option value="manager">Manager</option>
          </select>
        </div>
      </div>
    </div>
  );
}

function reorder(list) {
  return list.map((it, i) => ({ ...it, order: i + 1 }));
}

// ---- inline styles (kept local to avoid a new CSS file) -----------------
const sectionStyle = {
  background: '#fff',
  border: '1px solid #e5e5e5',
  borderRadius: 12,
  padding: 16,
  marginBottom: 16,
};
const h2Style = { fontFamily: 'Fraunces, Georgia, serif', fontSize: '1.1rem', margin: '0 0 12px 0' };
const fieldRow = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 };
const labelStyle = { fontSize: '0.8rem', fontWeight: 600, color: '#333' };
const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #ccc',
  fontSize: '0.9rem',
  fontFamily: 'inherit',
  background: '#fff',
};
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
const dialogStyle = {
  background: '#fff', borderRadius: 12, padding: 20,
  width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'auto',
};
